/* global mixpanel, optimoveSDK, pintrk, _tfa, fbq, obApi */
import { getItem } from 'js/storeManager'
import { environment } from 'js/config'
import { isSSR } from 'js/isSSR'

const createAdapter = (test, methods) => {
  const adapter = {}

  // Create noop tracking functions when server rendering (prerendering)
  if (
    (typeof navigator !== 'undefined' &&
      navigator.userAgent.match('DisableForTesting')) ||
    isSSR
  ) {
    Object.keys(methods).map(key => {
      adapter[key] = () => {}
    })
  } else {
    const init = new Promise(resolve => {
      const interval = setInterval(() => {
        if (test()) {
          resolve()
          clearInterval(interval)
        }
      }, 50)
    })

    Object.keys(methods).map(key => {
      adapter[key] = (...params) => init.then(() => methods[key](...params))
    })
  }
  return adapter
}

export default {
  mixpanel: createAdapter(
    () => {
      if (typeof mixpanel === 'undefined') return
      if (environment !== 'production' && !getItem('mixpanel')) return
      return window.mixpanel && window.mixpanel.__loaded
    },
    {
      alias(userId) {
        if (userId) mixpanel.alias(userId)
      },
      people(userId, userProperties, options) {
        if (userId) mixpanel.identify(userId)

        if (userProperties) {
          if (options && options.setOnce) {
            // Set people properties on our identified user, but only if they have not yet been set.
            mixpanel.people.set_once(userProperties)
          } else {
            // Set people properties on our identified user
            mixpanel.people.set(userProperties)
          }
        }
      },
      track(eventName, eventProperties) {
        if (eventName) mixpanel.track(eventName, eventProperties)
      },
      register(superProperties) {
        mixpanel.register(superProperties)
      },
      reset() {
        mixpanel.reset()
      },
    }
  ),

  ga: createAdapter(() => !!window.ga, {
    page({ url, pathname, title }) {
      ga('set', 'page', pathname)
      ga('set', 'location', url)
      ga('set', 'title', title)
      ga('send', 'pageview')
    },
    track(eventAction, eventProperties = {}) {
      if (
        !Object.prototype.hasOwnProperty.call(eventProperties, 'eventCategory')
      ) {
        eventProperties.eventCategory = 'All'
      }
      eventProperties.eventAction = eventAction
      eventProperties.hitType = 'event'
      ga('send', eventProperties)
    },
    set(dimension, value) {
      ga('set', dimension, value)
    },
  }),

  optimove: createAdapter(
    () =>
      !!window.optimoveSDK &&
      optimoveSDK.API &&
      window.__OPTIMOVE_INITIALIZED === true,
    {
      page({ url, title, category, language }) {
        try {
          optimoveSDK.API.setPageVisit(url, title, category)
          optimoveSDK.API.reportEvent('page_loaded', {
            page_url: url,
            page_title: title,
            page_category: category,
            page_language: language,
          })
        } catch (error) {
          // Silence
        }
      },
      track(event, parameters = {}) {
        try {
          optimoveSDK.API.reportEvent(event, parameters)
        } catch (error) {
          // Silence
        }
      },
      user({ id, email, eventName, parameters }) {
        if (id && email) {
          optimoveSDK.API.registerUser(String(id), email, eventName, parameters)
        } else if (id) {
          optimoveSDK.API.setUserId(String(id))
        } else if (email) {
          optimoveSDK.API.setUserEmail(email)
        }
      },
    }
  ),

  pinterest: createAdapter(
    () => !navigator.userAgent.match('DisableForTesting') && !!window.pintrk,
    {
      page() {
        pintrk('page')
      },
      track(event) {
        pintrk('track', event)
      },
    }
  ),

  taboola: createAdapter(() => !!window._tfa, {
    page() {
      _tfa.push({ notify: 'event', name: 'page_view', id: 1157532 })
    },
  }),

  facebook: createAdapter(() => !!window.fbq, {
    page() {
      fbq('track', 'PageView')
    },
    track(eventName, parameters) {
      fbq('track', eventName, parameters)
    },
  }),

  outbrain: createAdapter(() => !!window.obApi, {
    page() {
      obApi('track', 'Clickouts')
    },
  }),

  revcontent: createAdapter(() => true, {
    track(eventName) {
      const img = document.createElement('img')
      let token
      switch (eventName) {
        case 'Purchase':
          token =
            'dS8Gl%2BdYH15Rh0pb6cLL9qbsFUruX%2B8xOrjbFcGHMLVhvy%2Bpt9S093AFmT373RPn'
          break
        case 'Install':
          token =
            '97ChjefPmChZGm07k%2BUhohbQk%2BjCumfTO4Fh7QwB23TBiV8CUOqTDm5wVptWivxP'
          break
        default:
          return
      }
      img.setAttribute('src', `//trends.revcontent.com/conv.php?t=${token}`)
      document.getElementsByTagName('body')[0].appendChild(img)
    },
  }),
}
